import React, { useState } from "react";
import { useShow, useCustomMutation } from "@refinedev/core";
import { Show } from "@refinedev/antd";
import {
  Card,
  Descriptions,
  Row,
  Col,
  Tag,
  Space,
  Button,
  Modal,
  InputNumber,
  Input,
  DatePicker,
  message,
  Alert,
} from "antd";
import dayjs from "dayjs";
import {
  PAYMENT_REQUEST_BY_ID_QUERY,
  COMPLETE_PAYMENT_REQUEST_PAYMENT_MUTATION,
  CANCEL_PAYMENT_REQUEST_MUTATION,
  FAIL_PAYMENT_REQUEST_MUTATION,
} from "./queries";
import { PaymentRequestStatus, PaymentRequestPriority, Attachment } from "./interface";
import { FilePdfOutlined, FileImageOutlined, FileTextOutlined, FileOutlined } from "@ant-design/icons";
import { UserInfo } from "../../components/UserInfo";

const StatusBadge: React.FC<{ status: PaymentRequestStatus }> = ({ status }) => {
  const colorMap: Record<PaymentRequestStatus, string> = {
    [PaymentRequestStatus.DRAFT]: "default",
    [PaymentRequestStatus.SUBMITTED]: "blue",
    [PaymentRequestStatus.COMPLETED]: "green",
    [PaymentRequestStatus.FAILED]: "red",
    [PaymentRequestStatus.CANCELLED]: "orange",
  };
  return <Tag color={colorMap[status] || "default"}>{status}</Tag>;
};

const PriorityBadge: React.FC<{ priority: PaymentRequestPriority }> = ({ priority }) => {
  const colorMap: Record<PaymentRequestPriority, string> = {
    [PaymentRequestPriority.LOW]: "green",
    [PaymentRequestPriority.MEDIUM]: "blue",
    [PaymentRequestPriority.HIGH]: "orange",
    [PaymentRequestPriority.URGENT]: "red",
  };
  return <Tag color={colorMap[priority] || "default"}>{priority}</Tag>;
};

// Helper function to get appropriate icon based on mimeType
const getMimeTypeIcon = (mimeType: string) => {
  if (mimeType.includes("pdf")) {
    return <FilePdfOutlined />;
  } else if (mimeType.includes("image")) {
    return <FileImageOutlined />;
  } else if (mimeType.includes("text")) {
    return <FileTextOutlined />;
  }
  return <FileOutlined />;
};

// Helper function to format file size
const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

export const ShowPaymentRequest: React.FC = () => {
  const [isCompleteOpen, setIsCompleteOpen] = useState(false);
  const [paymentVoucherNumber, setPaymentVoucherNumber] = useState<string>("");
  const [paymentPaidAmount, setPaymentPaidAmount] = useState<number | undefined>(undefined);
  const [paymentDate, setPaymentDate] = useState<dayjs.Dayjs | null>(dayjs());
  const [paymentMethod, setPaymentMethod] = useState<string>("");
  const [paymentReference, setPaymentReference] = useState<string>("");

  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState<string>("");

  const [isFailOpen, setIsFailOpen] = useState(false);
  const [failReason, setFailReason] = useState<string>("");

  const { query } = useShow({
    resource: "paymentRequests",
    meta: {
      gqlQuery: PAYMENT_REQUEST_BY_ID_QUERY,
    },
  });

  const { data, isLoading } = query;
  const record = data?.data;

  const { mutate: completeMutate, isLoading: completing } = useCustomMutation();
  const { mutate: cancelMutate, isLoading: cancelling } = useCustomMutation();
  const { mutate: failMutate, isLoading: failing } = useCustomMutation();

  const handleComplete = async () => {
    if (!record?.id) return;

    completeMutate(
      {
        url: "",
        method: "post",
        values: {
          input: {
            paymentRequestId: record.id,
            payment: {
              voucherNumber: paymentVoucherNumber,
              paidAmount: paymentPaidAmount ?? record.amount,
              paymentDate: paymentDate?.toISOString(),
              method: paymentMethod,
              reference: paymentReference,
            },
          },
        },
        meta: { gqlMutation: COMPLETE_PAYMENT_REQUEST_PAYMENT_MUTATION },
      },
      {
        onSuccess: () => {
          message.success("Payment completed successfully");
          setIsCompleteOpen(false);
          query.refetch();
        },
        onError: (e) => {
          console.error(e);
          message.error("Failed to complete payment");
        },
      },
    );
  };

  const handleCancel = async () => {
    if (!record?.id) return;

    cancelMutate(
      {
        url: "",
        method: "post",
        values: {
          input: {
            paymentRequestId: record.id,
            reason: cancelReason || undefined,
          },
        },
        meta: { gqlMutation: CANCEL_PAYMENT_REQUEST_MUTATION },
      },
      {
        onSuccess: () => {
          message.success("Payment request cancelled successfully");
          setIsCancelOpen(false);
          setCancelReason("");
          query.refetch();
        },
        onError: (e) => {
          console.error(e);
          message.error("Failed to cancel payment request");
        },
      },
    );
  };

  const handleFail = async () => {
    if (!record?.id) return;

    failMutate(
      {
        url: "",
        method: "post",
        values: {
          input: {
            paymentRequestId: record.id,
            reason: failReason || undefined,
          },
        },
        meta: { gqlMutation: FAIL_PAYMENT_REQUEST_MUTATION },
      },
      {
        onSuccess: () => {
          message.success("Payment request marked as failed");
          setIsFailOpen(false);
          setFailReason("");
          query.refetch();
        },
        onError: (e) => {
          console.error(e);
          message.error("Failed to mark payment request as failed");
        },
      },
    );
  };

  const getApiBaseUrl = () => {
    return import.meta.env.VITE_API_URL;
  };

  const filePreviewUrl = (fileId: string) => {
    return `${getApiBaseUrl()}/file-storage/preview/${fileId}`;
  };

  const canSubmit = record?.status === PaymentRequestStatus.DRAFT;
  const canComplete = record?.status === PaymentRequestStatus.SUBMITTED;
  const canCancel = record?.status === PaymentRequestStatus.DRAFT || record?.status === PaymentRequestStatus.SUBMITTED || record?.status === PaymentRequestStatus.FAILED;
  const canFail = record?.status === PaymentRequestStatus.SUBMITTED;

  return (
    <Show
      isLoading={isLoading}
      title={record ? `Payment Request • ${record.invoiceId}` : "Payment Request"}
      headerButtons={
        record && (
          <Space>
            {canSubmit && (
              <Button type="primary" onClick={() => window.location.href = `/payment-requests/edit/${record.id}`}>
                Edit
              </Button>
            )}
            {canComplete && (
              <Button type="primary" onClick={() => setIsCompleteOpen(true)}>
                Complete Payment
              </Button>
            )}
            {canCancel && (
              <Button danger onClick={() => setIsCancelOpen(true)}>
                Cancel
              </Button>
            )}
            {canFail && (
              <Button danger onClick={() => setIsFailOpen(true)}>
                Mark as Failed
              </Button>
            )}
          </Space>
        )
      }
    >
      {record && (
        <>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card title="Basic Information">
                <Descriptions column={2} layout="vertical" bordered>
                  <Descriptions.Item label="Payment Request ID">{record.id}</Descriptions.Item>
                  <Descriptions.Item label="Invoice ID">{record.invoiceId}</Descriptions.Item>
                  <Descriptions.Item label="PO Number">{record.poNo}</Descriptions.Item>
                  <Descriptions.Item label="Receiving Number">{record.receivingNo}</Descriptions.Item>
                  <Descriptions.Item label="Status"><StatusBadge status={record.status} /></Descriptions.Item>
                  <Descriptions.Item label="Priority"><PriorityBadge priority={record.priority} /></Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>

            <Col span={24}>
              <Card title="Supplier Information">
                <Descriptions column={2} layout="vertical" bordered>
                  <Descriptions.Item label="Supplier ID">{record.supplierId}</Descriptions.Item>
                  <Descriptions.Item label="Supplier Name">{record.supplierName}</Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>

            <Col span={24}>
              <Card title="Payment Details">
                <Descriptions column={2} layout="vertical" bordered>
                  <Descriptions.Item label="Amount">
                    {record.currency} {Number(record.amount || 0).toLocaleString()}
                  </Descriptions.Item>
                  <Descriptions.Item label="Currency">{record.currency}</Descriptions.Item>
                  <Descriptions.Item label="Exchange Rate">{record.exchangeRate}</Descriptions.Item>
                  <Descriptions.Item label="Due Date">
                    {record.dueDate ? dayjs(record.dueDate).format("YYYY-MM-DD") : "-"}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>

            <Col span={24}>
              <Card title="Bank Account Details">
                <Descriptions column={2} layout="vertical" bordered>
                  <Descriptions.Item label="Bank">{record.bankAccountDetails.bank}</Descriptions.Item>
                  <Descriptions.Item label="Account Number">{record.bankAccountDetails.accountNo}</Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>

            {record.status === PaymentRequestStatus.COMPLETED && record.payment && (
              <Col span={24}>
                <Card title="Payment Information">
                  <Descriptions column={2} layout="vertical" bordered>
                    <Descriptions.Item label="Voucher Number">{record.payment.voucherNumber || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Paid Amount">
                      {record.currency} {Number(record.payment.paidAmount || 0).toLocaleString()}
                    </Descriptions.Item>
                    <Descriptions.Item label="Payment Date">
                      {record.payment.paymentDate ? dayjs(record.payment.paymentDate).format("YYYY-MM-DD") : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Payment Method">{record.payment.method || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Reference" span={2}>{record.payment.reference || "-"}</Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>
            )}

            <Col span={24}>
              <Card title="Attachments">
                <Space wrap>
                  {(record.attachments || []).map((attachment: Attachment) => {
                    return (
                      <div key={attachment.fileId} style={{ border: "1px solid #eee", padding: 8, borderRadius: 6 }}>
                        <Space>
                          {getMimeTypeIcon(attachment.mimeType)}
                          <a href={filePreviewUrl(attachment.fileId)} target="_blank" rel="noreferrer">
                            {attachment.originalName} {attachment.size > 0 && `(${formatBytes(attachment.size)})`}
                          </a>
                        </Space>
                      </div>
                    );
                  })}
                  {!record.attachments?.length && <div>No attachments</div>}
                </Space>
              </Card>
            </Col>

            <Col span={24}>
              <Card title="Additional Information">
                <Descriptions column={2} layout="vertical" bordered>
                  <Descriptions.Item label="Notes" span={2}>{record.notes || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Tags" span={2}>{(record.tags || []).join(", ") || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Team ID">{record.teamId || "-"}</Descriptions.Item>
                   <Descriptions.Item label="Created By">
                     {record.createdBy ? <UserInfo userId={record.createdBy} /> : "-"}
                   </Descriptions.Item>
                  <Descriptions.Item label="Created At">
                    {record.createdAt ? dayjs(record.createdAt).format("YYYY-MM-DD HH:mm") : "-"}
                  </Descriptions.Item>
                   <Descriptions.Item label="Updated By">
                     {record.updatedBy ? <UserInfo userId={record.updatedBy} /> : "-"}
                   </Descriptions.Item>
                  <Descriptions.Item label="Updated At">
                    {record.updatedAt ? dayjs(record.updatedAt).format("YYYY-MM-DD HH:mm") : "-"}
                  </Descriptions.Item>
                   {record.submittedBy && (
                     <>
                       <Descriptions.Item label="Submitted By">
                         <UserInfo userId={record.submittedBy} />
                       </Descriptions.Item>
                      <Descriptions.Item label="Submitted At">
                        {record.submittedAt ? dayjs(record.submittedAt).format("YYYY-MM-DD HH:mm") : "-"}
                      </Descriptions.Item>
                    </>
                  )}
                   {record.completedBy && (
                     <>
                       <Descriptions.Item label="Completed By">
                         <UserInfo userId={record.completedBy} />
                       </Descriptions.Item>
                      <Descriptions.Item label="Completed At">
                        {record.completedAt ? dayjs(record.completedAt).format("YYYY-MM-DD HH:mm") : "-"}
                      </Descriptions.Item>
                    </>
                  )}
                   {record.cancelledBy && (
                     <>
                       <Descriptions.Item label="Cancelled By">
                         <UserInfo userId={record.cancelledBy} />
                       </Descriptions.Item>
                      <Descriptions.Item label="Cancelled At">
                        {record.cancelledAt ? dayjs(record.cancelledAt).format("YYYY-MM-DD HH:mm") : "-"}
                      </Descriptions.Item>
                    </>
                  )}
                </Descriptions>
              </Card>
            </Col>
          </Row>

          <Modal
            title="Complete Payment"
            open={isCompleteOpen}
            onOk={handleComplete}
            onCancel={() => setIsCompleteOpen(false)}
            confirmLoading={completing}
            okText="Complete"
            width={600}
          >
            <Space direction="vertical" style={{ width: "100%" }}>
              <Alert
                message="Complete Payment"
                description="Enter the payment details to complete this payment request."
                type="info"
                style={{ marginBottom: 16 }}
              />
              <div>Voucher Number</div>
              <Input
                value={paymentVoucherNumber}
                onChange={(e) => setPaymentVoucherNumber(e.target.value)}
                placeholder="Enter voucher number"
              />
              <div>Paid Amount</div>
              <InputNumber
                value={paymentPaidAmount}
                onChange={(v) => setPaymentPaidAmount(Number(v || 0))}
                min={0}
                step={0.01}
                style={{ width: "100%" }}
                placeholder={`Default: ${record?.amount}`}
              />
              <div>Payment Date</div>
              <DatePicker
                style={{ width: "100%" }}
                value={paymentDate}
                onChange={setPaymentDate}
              />
              <div>Payment Method</div>
              <Input
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                placeholder="Enter payment method (e.g., Bank Transfer, Cash)"
              />
              <div>Reference</div>
              <Input
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
                placeholder="Enter payment reference"
              />
            </Space>
          </Modal>

          <Modal
            title="Cancel Payment Request"
            open={isCancelOpen}
            onOk={handleCancel}
            onCancel={() => setIsCancelOpen(false)}
            confirmLoading={cancelling}
            okText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Space direction="vertical" style={{ width: "100%" }}>
              <Alert
                message="Are you sure?"
                description="This action will cancel the payment request. This cannot be undone."
                type="warning"
                style={{ marginBottom: 16 }}
              />
              <div>Reason (Optional)</div>
              <Input.TextArea
                rows={3}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Enter reason for cancellation"
              />
            </Space>
          </Modal>

          <Modal
            title="Mark as Failed"
            open={isFailOpen}
            onOk={handleFail}
            onCancel={() => setIsFailOpen(false)}
            confirmLoading={failing}
            okText="Mark as Failed"
            okButtonProps={{ danger: true }}
          >
            <Space direction="vertical" style={{ width: "100%" }}>
              <Alert
                message="Are you sure?"
                description="This action will mark the payment request as failed. This cannot be undone."
                type="warning"
                style={{ marginBottom: 16 }}
              />
              <div>Reason (Optional)</div>
              <Input.TextArea
                rows={3}
                value={failReason}
                onChange={(e) => setFailReason(e.target.value)}
                placeholder="Enter reason for failure"
              />
            </Space>
          </Modal>
        </>
      )}
    </Show>
  );
};
